import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, MessageSquare, FileText, Download, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import useStore from '@/store/useStore';
import FeedbackList from './FeedbackList';
import FeedbackForm from './FeedbackForm';
import ReportList from './ReportList';
import ReportForm from './ReportForm';
import { Feedback, Report } from '@/types/cricket';

const FeedbackPage = () => {
  const { feedback, reports, fetchFeedback, fetchReports, loading } = useStore();
  const [activeTab, setActiveTab] = useState('feedback');
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);

  useEffect(() => {
    fetchFeedback();
    fetchReports();
  }, [fetchFeedback, fetchReports]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'in_progress':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'closed':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getReportStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'generating':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getReportStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'generating':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const openFeedbackForm = (feedback?: Feedback) => {
    setSelectedFeedback(feedback || null);
    setShowFeedbackForm(true);
  };

  const openReportForm = (report?: Report) => {
    setSelectedReport(report || null);
    setShowReportForm(true);
  };

  const closeFeedbackForm = () => {
    setShowFeedbackForm(false);
    setSelectedFeedback(null);
  };

  const closeReportForm = () => {
    setShowReportForm(false);
    setSelectedReport(null);
  };

  const feedbackStats = {
    total: feedback.length,
    open: feedback.filter(f => f.status === 'open').length,
    inProgress: feedback.filter(f => f.status === 'in_progress').length,
    resolved: feedback.filter(f => f.status === 'resolved').length,
  };

  const reportStats = {
    total: reports.length,
    pending: reports.filter(r => r.status === 'pending').length,
    generating: reports.filter(r => r.status === 'generating').length,
    completed: reports.filter(r => r.status === 'completed').length,
    failed: reports.filter(r => r.status === 'failed').length,
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Feedback & Reports</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Submit feedback, track issues, and generate reports
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="feedback" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Feedback
            <Badge variant="secondary" className="ml-2">
              {feedbackStats.total}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Reports
            <Badge variant="secondary" className="ml-2">
              {reportStats.total}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="feedback" className="space-y-6">
          {/* Feedback Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
                    <p className="text-2xl font-bold">{feedbackStats.total}</p>
                  </div>
                  <MessageSquare className="h-8 w-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open</p>
                    <p className="text-2xl font-bold text-blue-600">{feedbackStats.open}</p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">In Progress</p>
                    <p className="text-2xl font-bold text-yellow-600">{feedbackStats.inProgress}</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-yellow-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Resolved</p>
                    <p className="text-2xl font-bold text-green-600">{feedbackStats.resolved}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Feedback Actions */}
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Feedback</h2>
            <Button onClick={() => openFeedbackForm()} className="bg-cricket-green">
              <Plus className="h-4 w-4 mr-2" />
              New Feedback
            </Button>
          </div>

          {/* Feedback List */}
          <FeedbackList 
            feedback={feedback} 
            loading={loading.feedback}
            onEdit={openFeedbackForm}
          />
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          {/* Report Stats */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
                    <p className="text-2xl font-bold">{reportStats.total}</p>
                  </div>
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                    <p className="text-2xl font-bold text-blue-600">{reportStats.pending}</p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Generating</p>
                    <p className="text-2xl font-bold text-yellow-600">{reportStats.generating}</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-yellow-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed</p>
                    <p className="text-2xl font-bold text-green-600">{reportStats.completed}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Failed</p>
                    <p className="text-2xl font-bold text-red-600">{reportStats.failed}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Report Actions */}
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Reports</h2>
            <Button onClick={() => openReportForm()} className="bg-cricket-green">
              <Plus className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </div>

          {/* Report List */}
          <ReportList 
            reports={reports} 
            loading={loading.reports}
            onEdit={openReportForm}
          />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {showFeedbackForm && (
        <FeedbackForm 
          feedback={selectedFeedback}
          onClose={closeFeedbackForm}
          onSuccess={() => {
            closeFeedbackForm();
            fetchFeedback();
          }}
        />
      )}

      {showReportForm && (
        <ReportForm 
          report={selectedReport}
          onClose={closeReportForm}
          onSuccess={() => {
            closeReportForm();
            fetchReports();
          }}
        />
      )}
    </div>
  );
};

export default FeedbackPage; 